/**
 * STL File Parser
 *
 * Parses STL files (binary or ASCII) and calculates:
 * - Volume (cm³)
 * - Bounding box dimensions (cm)
 * - Surface area (cm²)
 * - Triangle count
 */

import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';

export interface STLAnalysis {
  volumeCm3: number;
  boundingBox: {
    x: number; // cm
    y: number; // cm
    z: number; // cm
  };
  surfaceAreaCm2: number;
  triangleCount: number;
  isValid: boolean;
  error?: string;
}

/**
 * Parse an STL file and extract geometry data
 */
export async function parseSTL(file: File): Promise<STLAnalysis> {
  try {
    // Validate file
    if (!file.name.toLowerCase().endsWith('.stl')) {
      return {
        volumeCm3: 0,
        boundingBox: { x: 0, y: 0, z: 0 },
        surfaceAreaCm2: 0,
        triangleCount: 0,
        isValid: false,
        error: 'File must be an STL file',
      };
    }

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();

    // Load STL using three.js
    const loader = new STLLoader();
    const geometry = loader.parse(arrayBuffer);

    if (!geometry || !geometry.attributes.position) {
      return {
        volumeCm3: 0,
        boundingBox: { x: 0, y: 0, z: 0 },
        surfaceAreaCm2: 0,
        triangleCount: 0,
        isValid: false,
        error: 'Invalid STL geometry',
      };
    }

    // Calculate bounding box
    geometry.computeBoundingBox();
    const bbox = geometry.boundingBox!;

    // Convert from mm to cm (STL files are typically in mm)
    const dimensions = {
      x: (bbox.max.x - bbox.min.x) / 10, // mm to cm
      y: (bbox.max.y - bbox.min.y) / 10,
      z: (bbox.max.z - bbox.min.z) / 10,
    };

    // Calculate volume using signed tetrahedron method
    const volume = calculateVolume(geometry);
    const volumeCm3 = Math.abs(volume) / 1000; // mm³ to cm³

    // Calculate surface area
    const surfaceArea = calculateSurfaceArea(geometry);
    const surfaceAreaCm2 = surfaceArea / 100; // mm² to cm²

    // Triangle count
    const triangleCount = geometry.attributes.position.count / 3;

    return {
      volumeCm3,
      boundingBox: dimensions,
      surfaceAreaCm2,
      triangleCount,
      isValid: true,
    };
  } catch (error) {
    return {
      volumeCm3: 0,
      boundingBox: { x: 0, y: 0, z: 0 },
      surfaceAreaCm2: 0,
      triangleCount: 0,
      isValid: false,
      error: error instanceof Error ? error.message : 'Failed to parse STL file',
    };
  }
}

/**
 * Calculate volume using signed tetrahedron method
 * Each triangle forms a tetrahedron with the origin
 */
function calculateVolume(geometry: THREE.BufferGeometry): number {
  const positions = geometry.attributes.position;
  let volume = 0;

  // Iterate through triangles
  for (let i = 0; i < positions.count; i += 3) {
    const v1 = new THREE.Vector3(positions.getX(i), positions.getY(i), positions.getZ(i));
    const v2 = new THREE.Vector3(
      positions.getX(i + 1),
      positions.getY(i + 1),
      positions.getZ(i + 1)
    );
    const v3 = new THREE.Vector3(
      positions.getX(i + 2),
      positions.getY(i + 2),
      positions.getZ(i + 2)
    );

    // Signed volume of tetrahedron = (v1 · (v2 × v3)) / 6
    volume += v1.dot(v2.cross(v3)) / 6;
  }

  return volume;
}

/**
 * Calculate surface area by summing triangle areas
 */
function calculateSurfaceArea(geometry: THREE.BufferGeometry): number {
  const positions = geometry.attributes.position;
  let area = 0;

  for (let i = 0; i < positions.count; i += 3) {
    const v1 = new THREE.Vector3(positions.getX(i), positions.getY(i), positions.getZ(i));
    const v2 = new THREE.Vector3(
      positions.getX(i + 1),
      positions.getY(i + 1),
      positions.getZ(i + 1)
    );
    const v3 = new THREE.Vector3(
      positions.getX(i + 2),
      positions.getY(i + 2),
      positions.getZ(i + 2)
    );

    // Area of triangle = 0.5 * ||(v2 - v1) × (v3 - v1)||
    const edge1 = v2.clone().sub(v1);
    const edge2 = v3.clone().sub(v1);
    const cross = edge1.cross(edge2);
    area += cross.length() / 2;
  }

  return area;
}
